import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Button, Card, InnerPageToggleButton } from '@wajeez-web/components/ui';
import { FormSectionTitle } from '@wajeez-web/modules/content';
import { useAuthSelector } from '@wajeez-web/stores/auth';
import { useForm, FieldValues, DeepPartial, Path } from 'react-hook-form';

import { FormFieldV2 } from './Types';
import WajeezFormInputV2 from './WajeezFormInputV2';

export interface WajeezFormBuilderProps<TFormFields> {
  title?: string;
  desc?: string;
  formValues?: DeepPartial<TFormFields>;
  onSubmit: (props: TFormFields) => void;
  sections: {
    title: string;
    rows: { [key in keyof TFormFields]?: FormFieldV2 };
  }[];
  withoutCard?: boolean;
  hasToggleButton?: boolean;
  isUpdate?: boolean;
}

const WajeezFormBuilderSectioned = <TFormFields extends FieldValues>({
  title,
  desc,
  formValues,
  onSubmit,
  sections,
  withoutCard,
  hasToggleButton,
  isUpdate,
}: WajeezFormBuilderProps<TFormFields>) => {
  const navigate = useNavigate();
  const permissions = useAuthSelector((s) => s.auth.permission ?? []);

  const { t } = useTranslation();

  const { handleSubmit, control, setError, watch } = useForm<TFormFields>({
    defaultValues: formValues,
    mode: 'all',
    shouldFocusError: true,
  });

  // watch((data) => {
  //   console.log(data);
  // });

  const getFormRows = () => {
    return (
      <div className="space-y-8">
        {title && (
          <div className="">
            <h3 className="ms-2 lg:ms-0 text-2xl flex align-middle font-bold space-s-2 tracking-tight leading-none  ">
              {hasToggleButton && <InnerPageToggleButton />}{' '}
              <div className="mt-1">{title}</div>
            </h3>
            <p className=" text-sm text-gray-600">{desc}</p>
          </div>
        )}
        <div className="space-y-6 h-[350px] overflow-y-auto pl-4">
          {sections.map((s, i) => {
            if (Object.keys(s.rows).length) {
              return (
                <div key={s.title}>
                  <div>
                    <FormSectionTitle title={s.title} />
                  </div>
                  <div>
                    <div
                      className={`grid grid-cols-2 gap-3 md:gap-x-16 pb-8 ${
                        i !== sections.length - 1 && 'border-b'
                      }`}
                    >
                      {Object.entries(s.rows)
                        .sort(
                          (a, b) =>
                            (a[1]?.order ?? 99999) - (b[1]?.order ?? 99999)
                        )
                        .map(({ '0': key, '1': value }, i) => {
                          const name = key as Path<TFormFields>;
                          const item = value as FormFieldV2;
                          return (
                            <div
                              key={key}
                              tabIndex={i}
                              className={`col-span-2 md:col-span-1`}
                            >
                              <WajeezFormInputV2
                                control={control}
                                name={name}
                                fieldMeta={item}
                                isReadOnly={
                                  item.permission &&
                                  !permissions.includes(item.permission)
                                    ? true
                                    : false
                                }
                                setError={setError}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              );
            } else {
              return null;
            }
          })}
        </div>
      </div>
    );
  };

  return withoutCard ? (
    <div className="flex flex-col overflow-hidden space-y-6">
      {getFormRows()}
      <div className="p-4 border-t flex justify-end">
        <Button
          // refName={"create-book"}
          title="save"
          onClick={handleSubmit(onSubmit)}
          bg="primary"
          color="white"
          loading={isUpdate}
          disabled={isUpdate}
        />
      </div>
    </div>
  ) : (
    <Card
      cardBottom={
        <div className="p-4 bg-gray-100 flex justify-end space-s-2 rounded-md">
          <Button
            // refName={"create-book"}
            title="Cancel"
            onClick={() => {
              Swal.fire({
                title: t(`general.${'Are you sure?'}`, '') as string,
                text: t(
                  `general.${"You won't be able to revert this!"}`,
                  ''
                ) as string,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: t(`general.${'Yes'}`, '') as string,
                cancelButtonText: t(`general.${'Cancel'}`, '') as string,
              }).then((result) => {
                if (result.isConfirmed) {
                  navigate(-1);
                }
              });
            }}
            bg="white"
            color="textPrimary"
          />
          <Button
            // refName={"create-book"}
            title="save"
            onClick={handleSubmit(onSubmit)}
            bg="primary"
            color="white"
            loading={isUpdate}
            disabled={isUpdate}
          />
        </div>
      }
    >
      {getFormRows()}
    </Card>
  );
};

export default WajeezFormBuilderSectioned;
